import React from "react";

import {
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line
} from "recharts";

import { DataPointCard } from "../DataPointCard";
import { AnalyticsCard } from "../AnalyticsCard";
import { HollowTable, HollowTableRow } from "../HollowTable";
import { Image } from "../../atoms/Image";
import { Icon } from "../../atoms/Icon";

import useReportingOverview from "./ReportingOverviewHooks";
import { useHistory } from "react-router-dom";
import styles from "./ReportingOverview.module.scss";
import FormatHelpers from "./FormatHelpers";

const ReportingOverview = ({ overviewReport, dateRange }) => {
  const history = useHistory();
  const {
    totalViewsGraphData,
    totalViewsColors,
    totalSalesGraphData,
    totalSalesColors,
    videoListTableData,
    productListTableData,
    videoOriginListTableData,
    averageNumberOfViews,
    numberOfPinkButtonClicks,
    numberOfCompletedPurchases
  } = useReportingOverview(overviewReport, dateRange);

  return (
    <div className={styles.reportingOverviewContainer}>
      <div className={styles.dataPointCardsContainer}>
        <DataPointCard
          color="#09A496"
          iconName="grouped-bar-chart"
          title="AVG # OF VIEWS"
          value={averageNumberOfViews}
        />
        <DataPointCard
          color="#FF5D78"
          iconName="tag"
          title="Purchase Intent Clicks"
          value={numberOfPinkButtonClicks}
        />
        <DataPointCard
          color="#09A496"
          iconName="timeline-line-chart"
          title="COMPLETED PURCHASES"
          value={numberOfCompletedPurchases}
        />
      </div>
      <div className={styles.analyticsCardsRow}>
        <AnalyticsCard title="Total Views">
          <LineChart width={580} height={285} data={totalViewsGraphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              type="number"
              domain={["auto", "auto"]}
              interval="preserveStartEnd"
              scale="time"
              tickFormatter={(unixTime) =>
                FormatHelpers.dateFormatter({
                  unixTime,
                  dateRange,
                  format: "MMM YY"
                })
              }
            />
            <YAxis type="number" allowDecimals={false} />
            <Tooltip
              labelFormatter={(unixTime) =>
                FormatHelpers.dateFormatter({ unixTime, dateRange })
              }
            />
            <Legend layout="vertical" align="right" verticalAlign />
            {Object.keys((({ date, ...x }) => x)(totalViewsGraphData[0])).map(
              (videoKey, index) => (
                <Line
                  key={videoKey}
                  type="monotone"
                  dataKey={videoKey}
                  stroke={totalViewsColors[index]}
                />
              )
            )}
          </LineChart>
        </AnalyticsCard>
        <AnalyticsCard title="Total Sales">
          <LineChart width={580} height={285} data={totalSalesGraphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              type="number"
              domain={["auto", "auto"]}
              interval="preserveStartEnd"
              scale="time"
              tickFormatter={(unixTime) =>
                FormatHelpers.dateFormatter({
                  unixTime,
                  dateRange,
                  format: "MMM YY"
                })
              }
            />
            <YAxis type="number" allowDecimals={false} />
            <Tooltip
              labelFormatter={(unixTime) =>
                FormatHelpers.dateFormatter({ unixTime, dateRange })
              }
            />
            <Legend layout="vertical" align="right" verticalAlign />
            {Object.keys((({ date, ...x }) => x)(totalSalesGraphData[0])).map(
              (productName, index) => (
                <Line
                  type="monotone"
                  dataKey={productName}
                  stroke={totalSalesColors[index]}
                />
              )
            )}
          </LineChart>
        </AnalyticsCard>
      </div>
      <div className={styles.analyticsCardsRow}>
        <AnalyticsCard title="Video List">
          <div className={styles.tableCardContainer}>
            <HollowTable
              titles={["", "Video Name", "Views", "Orders", "Sales"]}
            >
              {videoListTableData.map(
                ({ id, thumbnail, name, views, orders, sales }) => (
                  <HollowTableRow
                    key={id}
                    onClick={() => history.push(`/reporting/video/${id}`)}
                  >
                    <Image src={thumbnail} size={42} alt={name} />
                    <span className={styles.lightTableText}>{name}</span>
                    <span className={styles.darkTableText}>
                      <Icon name="eye-open" color="#506883" size={15} />
                      {views}
                    </span>
                    <span className={styles.darkTableText}>
                      <Icon name="Orders" color="#506883" size={15} />
                      {orders}
                    </span>
                    <span className={styles.darkTableText}>
                      <Icon name="dollar" color="#506883" size={15} />
                      {sales.toFixed(2)}
                    </span>
                  </HollowTableRow>
                )
              )}
            </HollowTable>
          </div>
        </AnalyticsCard>
        <AnalyticsCard title="Product List">
          <div className={styles.tableCardContainer}>
            <HollowTable
              titles={[
                "",
                "Product Name",
                "SKU",
                "Videos Created",
                "Video Views",
                "Total Earnings"
              ]}
            >
              {productListTableData.map(
                ({ id, thumbnail, name, sku, videos, views, sales }) => (
                  <HollowTableRow
                    key={id}
                    onClick={() => history.push(`/reporting/product/${id}`)}
                  >
                    <Image src={thumbnail} size={33} alt={name} circle />
                    <span className={styles.lightTableText}>{name}</span>
                    <span className={styles.darkTableText}>
                      {sku || "(Multiple SKUs)"}
                    </span>
                    <span className={styles.darkTableText}>
                      <Icon name="mobile-video" color="#506883" size={15} />
                      {videos}
                    </span>
                    <span className={styles.darkTableText}>
                      <Icon name="eye-open" color="#506883" size={15} />
                      {views}
                    </span>
                    <span className={styles.darkTableText}>
                      <Icon name="dollar" color="#506883" size={15} />
                      {sales.toFixed(2)}
                    </span>
                  </HollowTableRow>
                )
              )}
            </HollowTable>
          </div>
        </AnalyticsCard>
      </div>
      <div className={styles.analyticsCardsRow}>
        <AnalyticsCard title="Video Origins List">
          <div className={styles.tableCardContainer}>
            <HollowTable titles={["Views", "Video Origin"]}>
              {videoOriginListTableData.map(({ id, views, origin }) => (
                <HollowTableRow key={id}>
                  <span className={styles.darkTableText}>
                    <Icon name="eye-open" color="#506883" size={15} />
                    {views}
                  </span>
                  {origin === "Other" ? (
                    <p>Other</p>
                  ) : (
                    <a
                      href={
                        origin.startsWith("https://") ||
                        origin.startsWith("http://")
                          ? origin
                          : `https://${origin}`
                      }
                      target="_blank"
                    >
                      {origin}
                    </a>
                  )}
                </HollowTableRow>
              ))}
            </HollowTable>
          </div>
        </AnalyticsCard>
      </div>
    </div>
  );
};

export default ReportingOverview;
